/**
 * Top 10 Best Projected Probables per Ball — extracted from ConsolidatedVerdict.js.
 * Pure-render: receives `topProbables` object keyed by B1..B5/LS1/LS2.
 */
export default function TopProbablesBlock({ topProbables }) {
  if (!topProbables) return null;

  return (
    <section className="bg-white border-2 border-indigo-400 rounded-xl overflow-hidden shadow-xl" data-testid="top-probables-block">
      <header className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 px-5 py-3 text-white">
        <h2 className="font-bold text-base tracking-tight flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-yellow-300 text-indigo-900 rounded text-[11px] font-black">TOP 10</span>
          Best Projected Probables per Ball (B1–B5 + LS1, LS2)
        </h2>
        <p className="text-[11px] text-white/85 mt-0.5">
          Consensus-scored candidates aggregated across W3, W7, W17, W32, W41, W46, W53, W56, W63. Higher score = more signals agreeing. Out-of-range numbers penalised.
        </p>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 divide-x divide-gray-200">
        {['B1','B2','B3','B4','B5','LS1','LS2'].map((pos) => {
          const list = topProbables[pos] || [];
          const isLS = pos.startsWith('LS');
          return (
            <div key={pos} className="p-3" data-testid={`top-probables-${pos}`}>
              <div className={`text-center mb-2 pb-1.5 border-b-2 ${isLS ? 'border-amber-400' : 'border-indigo-400'}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${isLS ? 'text-amber-700' : 'text-indigo-700'}`}>
                  {isLS ? 'Lucky Star' : 'Main Ball'}
                </div>
                <div className={`text-lg font-black ${isLS ? 'text-amber-900' : 'text-indigo-900'}`}>{pos}</div>
              </div>
              {list.length === 0 ? (
                <div className="text-center text-[10px] text-gray-400 italic py-4">No scored candidates</div>
              ) : (
                <ol className="space-y-1.5">
                  {list.map((item, idx) => {
                    const isTop1 = idx === 0;
                    const isTop3 = idx < 3;
                    const scoreTier = item.score >= 15 ? 'elite' : item.score >= 10 ? 'strong' : item.score >= 5 ? 'normal' : 'weak';
                    const scoreBg = scoreTier === 'elite' ? 'bg-emerald-500 text-white' : scoreTier === 'strong' ? 'bg-emerald-100 text-emerald-800' : scoreTier === 'normal' ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-600';
                    const ballBg = isLS
                      ? (isTop1 ? 'bg-amber-400 text-amber-950 ring-2 ring-amber-600' : isTop3 ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-gray-700')
                      : (isTop1 ? 'bg-indigo-600 text-white ring-2 ring-indigo-300' : isTop3 ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700');
                    return (
                      <li key={item.number} className="flex items-center gap-1.5 group relative" data-testid={`prob-${pos}-${idx}`}>
                        <span className="text-[9px] font-mono text-gray-400 w-4 text-right">{idx + 1}.</span>
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-mono font-black text-sm ${ballBg}`}>
                          {item.number}
                        </span>
                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${scoreBg}`}>
                          {item.score}
                        </span>
                        <span className="text-[9px] text-gray-400">{item.reason_count}×</span>
                        {/* Tooltip on hover showing contributing reasons */}
                        <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-20 bg-slate-900 text-white text-[10px] rounded-lg shadow-2xl p-2 w-64 border border-slate-700">
                          <div className="font-bold text-yellow-300 mb-1">#{item.number} · score {item.score}</div>
                          {item.reasons.map((reason, ri) => (
                            <div key={`${item.number}-r-${ri}`} className="leading-tight text-white/90">{reason}</div>
                          ))}
                          {item.reason_count > item.reasons.length && (
                            <div className="text-white/50 italic mt-1">+{item.reason_count - item.reasons.length} more</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          );
        })}
      </div>
      <footer className="bg-gray-50 px-4 py-2 text-[10px] text-gray-600 border-t border-gray-200 flex items-center gap-3 flex-wrap" data-testid="top-probables-legend">
        <span><span className="inline-block w-3 h-3 rounded-full bg-emerald-500 align-middle mr-1"></span>ELITE (≥15)</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-emerald-200 align-middle mr-1"></span>STRONG (≥10)</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-sky-200 align-middle mr-1"></span>NORMAL (≥5)</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-gray-200 align-middle mr-1"></span>WEAK</span>
        <span className="ml-auto italic">Hover a row to see contributing signals</span>
      </footer>
    </section>
  );
}
